import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  makeStyles,
  Typography,
  Box
} from '@material-ui/core';
import { DragIndicator } from '@material-ui/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';
import * as courseApi from '../../api/courseApi';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 500,
  },
  dialogContent: {
    overflow: 'visible',
  },
  listContainer: {
    maxHeight: '60vh',
    overflow: 'auto',
  },
}));

function ReorderCoursesModal({ open, onClose, courses: initialCourses, onSuccess }) {
  const classes = useStyles();
  const [t] = useTranslation(['common']);
  const { enqueueSnackbar } = useSnackbar();
  const [courses, setCourses] = useState([]);

  // Update courses when initialCourses changes
  React.useEffect(() => {
    if (initialCourses && initialCourses.length > 0) {
      setCourses(initialCourses);
    }
  }, [initialCourses]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedItems = reorder(
      courses,
      result.source.index,
      result.destination.index
    );

    setCourses(reorderedItems);
  };

  const handleSave = async () => {
    try {
      // Create array of objects with id and order as required by the backend
      const coursesToReorder = courses.map((course, index) => ({
        id: course.id,
        order: index // Using zero-based index as the order number
      }));

      // Pass the array directly as the first argument
      await courseApi.reorderCourses(coursesToReorder, {});
      enqueueSnackbar(t('common:saveChanges'), { variant: 'success' });
      onSuccess();
      onClose();
    } catch (error) {
      enqueueSnackbar(t('common:updateFailed'), { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>{t('common:order')}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            {t('common:dragDrop')}
          </Typography>
        </Box>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className={classes.listContainer}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  <List>
                    {courses.map((course, index) => (
                      <Draggable
                        key={course.id}
                        draggableId={course.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              background: snapshot.isDragging ? "rgb(235,235,235)" : "inherit",
                            }}
                          >
                            <ListItem>
                              <ListItemIcon>
                                <DragIndicator />
                              </ListItemIcon>
                              <ListItemText primary={course.title} />
                            </ListItem>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
        >
          {t('common:save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReorderCoursesModal; 